import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface StickyScrollXProps {
  children: ReactNode;
  /**
   * Offset from the top of the viewport for the sticky scroll proxy.
   * Defaults to 0. Bump if there's another sticky header above this one.
   */
  stickyOffset?: number;
  /**
   * CSS max-height for the inner scroll container. When set, the table
   * scrolls vertically *inside* the bounded area rather than with the page,
   * which lets sticky `<th>` cells stay pinned to the viewport — sticky-y
   * inside an `overflow-x: auto` container otherwise bounds to the
   * container's own top edge, which moves with page scroll.
   */
  maxHeight?: string;
}

/**
 * Wraps wide horizontally-scrolling content (typically a `<table>`) and
 * adds a sticky scrollbar proxy at the top that stays glued to the viewport
 * as the user scrolls down through tall content. Solves the "scrollbar is
 * at the bottom of a 3000px-tall table, requires mouse-scrolling all the
 * way down to reach" problem for mouse users — especially on macOS, where
 * the scrollbar is also hidden by default.
 *
 * The proxy is a thin invisible spacer whose width matches the actual
 * content; scroll events on either container are synced to the other.
 * Pair with the `always-show-scrollbar` CSS class for the force-visible
 * styling (Mac mouse users can't see auto-hidden scrollbars).
 */
export function StickyScrollX({ children, stickyOffset = 0, maxHeight }: StickyScrollXProps) {
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  // Measure the actual rendered width of the content (the first child of
  // the bottom scroll container — typically the <table>) so the top proxy's
  // inner spacer matches and produces the same scroll range.
  useLayoutEffect(() => {
    const bottom = bottomRef.current;
    const target = bottom?.firstElementChild as HTMLElement | null;
    if (!target) return;
    const update = () => setContentWidth(target.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(target);
    return () => ro.disconnect();
  }, []);

  // Two-way scroll sync. The ignore-flag pair prevents the feedback loop
  // that would otherwise fire when assigning scrollLeft on one container
  // triggers the other's scroll listener.
  useEffect(() => {
    const top = topRef.current;
    const bottom = bottomRef.current;
    if (!top || !bottom) return;

    let ignoreTop = false;
    let ignoreBottom = false;
    const fromTop = () => {
      if (ignoreTop) { ignoreTop = false; return; }
      ignoreBottom = true;
      bottom.scrollLeft = top.scrollLeft;
    };
    const fromBottom = () => {
      if (ignoreBottom) { ignoreBottom = false; return; }
      ignoreTop = true;
      top.scrollLeft = bottom.scrollLeft;
    };

    top.addEventListener('scroll', fromTop, { passive: true });
    bottom.addEventListener('scroll', fromBottom, { passive: true });
    return () => {
      top.removeEventListener('scroll', fromTop);
      bottom.removeEventListener('scroll', fromBottom);
    };
  }, []);

  return (
    <>
      {/* Always render the proxy so the scroll listeners can attach on mount.
          The inner spacer width defaults to 100% until ResizeObserver fires,
          then matches the actual content width — only then does the
          horizontal scrollbar actually appear. rounded-t-xl matches the
          parent wrapper's corners so the wrapper can drop overflow-hidden
          (which would clip the sticky element). */}
      <div
        ref={topRef}
        className="overflow-x-auto sticky z-20 bg-white border-b border-gray-200 rounded-t-xl always-show-scrollbar"
        style={{ top: stickyOffset }}
        aria-hidden="true"
      >
        <div style={{ width: contentWidth || '100%', height: 1 }} />
      </div>
      <div
        ref={bottomRef}
        className={`${maxHeight ? 'overflow-auto' : 'overflow-x-auto'} rounded-b-xl always-show-scrollbar`}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {children}
      </div>
    </>
  );
}
